/* Version « particuliers » de la page Veille Marché.
   Copie autonome : elle peut diverger librement de la version pro. */
import { useEffect, useState } from "react";
import Seo from "@/components/v2/Seo";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FinalCTA } from "@/components/v2/blocks";
import PageHeroPhoto from "@/components/v2/PageHeroPhoto";
import { CardContent } from "@/components/ui/card";
import TraceCard from "@/components/v2/TraceCard";
import MarketSpotCard from "@/components/v2/MarketSpotCard";
import { ARTICLES, CATEGORIES } from "@/data/articles";
import api from "@/lib/api";

const TRACE_COLORS = ["#2E7D62", "#7AB317", "#0E9C8A", "#2F7DB8"];

export default function VeilleMarchePart() {
  const [articles, setArticles] = useState(ARTICLES);
  const [cat, setCat] = useState("Tous");

  useEffect(() => {
    api.get("/articles")
      .then((r) => { if (Array.isArray(r.data) && r.data.length) setArticles(r.data); })
      .catch(() => {});
  }, []);

  const filtered = cat === "Tous" ? articles : articles.filter((a) => a.category === cat);

  return (
    <div>
      <Seo title="Veille marché de l'énergie" description="Analyses, actualités et conseils sur le marché de l'électricité et du gaz pour mieux comprendre les évolutions des prix." path="/particuliers/veille-marche" />
      <PageHeroPhoto
        cmsKey="veille"
        eyebrow="Veille Marché"
        title="L'actualité de l'énergie, décryptée"
        subtitle="Le marché de l'énergie évolue en permanence. Retrouvez régulièrement nos analyses, actualités et conseils pour mieux comprendre les évolutions du secteur."
        image="/img/veille-part.jpg"
        overlay="cinematic-soft"
        parallax="fixed"
        flipLogo
        heroChart
        highlightsLabel="Au programme"
        highlights={[
          "Prix de l'électricité et du gaz",
          "Analyses et tendances du marché",
          "Réglementation et ACD",
          "Conseils particuliers & professionnels",
        ]}
      />

      <div className="em-container py-12">
        {/* Encart marché de gros (spot ENTSO-E) — contexte marché, ≠ tarif client */}
        <div className="mb-10 max-w-xl">
          <h2 className="em-eyebrow mb-3">Prix du marché de gros</h2>
          <MarketSpotCard />
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`rounded-full px-4 py-2 text-sm transition-colors ${cat === c ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((a, i) => (
            <TraceCard
              key={a.slug || i}
              index={i}
              radius={16}
              accent={TRACE_COLORS[i % TRACE_COLORS.length]}
              className="rounded-2xl border border-border bg-card text-card-foreground"
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="em-eyebrow">{a.category}</p>
                  {a.date && <p className="text-[10px] text-muted-foreground">{a.date}</p>}
                </div>
                <h3 className="font-display text-lg font-bold text-primary leading-snug">{a.title}</h3>
                <p className="text-sm text-muted-foreground mt-2">{a.excerpt}</p>
                <Link to={`/veille-marche/${a.slug}`} className="inline-flex items-center gap-1 text-sm text-primary mt-4 hover:underline">
                  Lire l'article <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </CardContent>
            </TraceCard>
          ))}
        </div>
        {!filtered.length && <p className="text-muted-foreground">Aucun article dans cette catégorie pour le moment.</p>}
      </div>

      <FinalCTA title="Une question sur le marché de l'énergie ?" />
    </div>
  );
}
